import type { OrgMembership, StockDimension, StockItem } from './types'

// Stock amounts in units. The server stores every amount in its dimension's canonical unit (L, kg,
// m3, or each) and accepts any unit below; these factors mirror server/src/core/units.ts (volume,
// mass, bulkVolume), which is the source of truth.

type UnitInfo = { factor: number; short: string; long: string }

export const STOCK_DIMENSION_UNITS: Record<StockDimension, { canonical: string; units: Record<string, UnitInfo> }> = {
  VOLUME: {
    canonical: 'L',
    units: {
      L: { factor: 1, short: 'L', long: 'Litres (L)' },
      mL: { factor: 0.001, short: 'mL', long: 'Millilitres (mL)' },
      us_gal: { factor: 3.785411784, short: 'US gal', long: 'US gallons' },
      imp_gal: { factor: 4.54609, short: 'imp gal', long: 'Imperial gallons' },
    },
  },
  MASS: {
    canonical: 'kg',
    units: {
      kg: { factor: 1, short: 'kg', long: 'Kilograms (kg)' },
      g: { factor: 0.001, short: 'g', long: 'Grams (g)' },
      lb: { factor: 0.45359237, short: 'lb', long: 'Pounds (lb)' },
      oz: { factor: 0.028349523125, short: 'oz', long: 'Ounces (oz)' },
      t: { factor: 1000, short: 't', long: 'Tonnes (t)' },
    },
  },
  BULK_VOLUME: {
    canonical: 'm3',
    units: {
      m3: { factor: 1, short: 'm³', long: 'Cubic metres (m³)' },
      yd3: { factor: 0.764554857984, short: 'yd³', long: 'Cubic yards (yd³)' },
    },
  },
  COUNT: { canonical: 'each', units: { each: { factor: 1, short: '', long: 'Each' } } },
}

export const STOCK_DIMENSION_LABEL: Record<StockDimension, { label: string; detail: string }> = {
  VOLUME: { label: 'Liquid', detail: 'L, mL, gallons' },
  MASS: { label: 'Dry', detail: 'kg, g, lb, tonnes' },
  BULK_VOLUME: { label: 'Bulk', detail: 'Mulch, compost: yd³, m³' },
  COUNT: { label: 'Count', detail: 'Traps, rolls, pieces' },
}

/** Mirrors DISPLAY_UNITS in server/src/core/units.ts. */
const ORG_DEFAULT: Record<OrgMembership['unitSystem'], Record<StockDimension, string>> = {
  IMPERIAL: { VOLUME: 'us_gal', MASS: 'lb', BULK_VOLUME: 'yd3', COUNT: 'each' },
  METRIC: { VOLUME: 'L', MASS: 'kg', BULK_VOLUME: 'm3', COUNT: 'each' },
}

export const orgStockUnit = (dim: StockDimension, system: OrgMembership['unitSystem'] | undefined) => ORG_DEFAULT[system ?? 'IMPERIAL'][dim]

export const unitsOf = (dim: StockDimension) => Object.keys(STOCK_DIMENSION_UNITS[dim].units)

const info = (dim: StockDimension, unit: string): UnitInfo => STOCK_DIMENSION_UNITS[dim].units[unit] ?? { factor: 1, short: unit, long: unit }

export const unitShort = (dim: StockDimension, unit: string) => info(dim, unit).short
export const unitLong = (dim: StockDimension, unit: string) => info(dim, unit).long

export const toCanonical = (dim: StockDimension, value: number, unit: string) => value * info(dim, unit).factor
export const fromCanonical = (dim: StockDimension, canonical: number, unit: string) => canonical / info(dim, unit).factor

/** "12.5 L", "3.3 US gal", "24" (counts have no unit). Up to `digits` decimals. */
export function formatIn(dim: StockDimension, value: number, unit: string, digits = 2): string {
  const n = value.toLocaleString(undefined, { maximumFractionDigits: digits })
  const short = unitShort(dim, unit)
  return short ? `${n} ${short}` : n
}

/** A canonical amount in the item's shown unit. */
export const formatAmount = (item: Pick<StockItem, 'dimension' | 'shownIn'>, canonical: number) =>
  formatIn(item.dimension, fromCanonical(item.dimension, canonical, item.shownIn), item.shownIn)

/** "1 jug", "3 jugs", "2 boxes". */
export function packages(name: string, n: number): string {
  const word = name.toLowerCase()
  const plural = /(s|x|ch|sh)$/.test(word) ? `${word}es` : `${word}s`
  return `${n.toLocaleString()} ${n === 1 ? word : plural}`
}

/** On hand as whole packages plus what is left in the package's unit ("3 jugs + 4.5 L"), or null with no package or less than one. */
export function packageBreakdown(item: Pick<StockItem, 'dimension' | 'onHand' | 'package'>): string | null {
  const pkg = item.package
  if (!pkg || item.onHand <= 0) return null
  const full = Math.floor(item.onHand / pkg.canonicalSize + 1e-9)
  if (full === 0) return null // less than one package: the amount alone says it
  const rest = fromCanonical(item.dimension, item.onHand - full * pkg.canonicalSize, pkg.unit)
  const parts = [full > 0 ? packages(pkg.name, full) : null, rest > 1e-6 ? formatIn(item.dimension, rest, pkg.unit) : null].filter(Boolean)
  return parts.join(' + ')
}

/** The canonical amount of an amount the person typed. */
export function amountToCanonical(item: Pick<StockItem, 'dimension' | 'package'>, amount: { packages: number | null; value: number | null; unit: string }) {
  return (amount.packages ?? 0) * (item.package?.canonicalSize ?? 0) + (amount.value !== null ? toCanonical(item.dimension, amount.value, amount.unit) : 0)
}

/** "3 jugs + 4.5 L", "2.5 US gal": what was typed for an entry. */
export function enteredText(dim: StockDimension, packageName: string | null, entered: { packages: number | null; value: number | null; unit: string | null }) {
  return [
    entered.packages !== null && packageName ? packages(packageName, entered.packages) : null,
    entered.value !== null && entered.unit ? formatIn(dim, entered.value, entered.unit, 4) : null,
  ]
    .filter(Boolean)
    .join(' + ')
}
